import React from 'react';
import { TextProcessor } from './TextProcessor';

interface ProcessedChildrenProps {
  children: React.ReactNode;
}

/**
 * A component that processes child components, replacing string children
 * with the result of compiling them as Handlebars templates.
 *
 * @param {ProcessedChildrenProps} props - The props of the component.
 * @returns {React.ReactElement} The processed child components.
 */
export const ProcessedChildren: React.FC<ProcessedChildrenProps> = ({ children }) => {
  /**
   * Recursively processes all child nodes, wrapping string children
   * with the TextProcessor component and recursively processing
   * the children of element nodes.
   *
   * @param {React.ReactNode} nodes - The nodes to process.
   * @returns {React.ReactNode} The processed nodes.
   */
  const processNodes = (nodes: React.ReactNode): React.ReactNode => {
    return React.Children.map(nodes, child => {
      if (child === null || child === undefined || child === false) {
        return null;
      }

      if (typeof child === 'string' || typeof child === 'number') {
        const text = String(child);
        return <TextProcessor text={text} />;
      } else if (React.isValidElement(child)) {
        const processedChildren = processNodes(child.props.children);
        return React.cloneElement(child, { ...child.props }, processedChildren);
      } else {
        return child; // Return other types of children unchanged
      }
    });
  };

  return <>{processNodes(children)}</>;
};
